import { useTranslation } from "react-i18next";
import GridDX from "../../../components/layout/griddx";
import Loading from "../../../components/layout/loading";
import PageTitle from "../../../components/layout/pagetitle";
import StaticDataGridDX from "../../../components/layout/staticdatagriddx";
import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import useReportService from "../../../shared/services/reportservice";
import { useAuthContext } from "../../../context/authcontext";
import { useNotificationContext } from "../../../context/notificationcontext";
import { usePDF } from "react-to-pdf";
import ButtonDX from "../../../components/controls/buttondx";
import { downloadPDF } from "../../../shared/globals";
import TypographyDX from "../../../components/layout/typographydx";
import SelectListDX from "../../../components/controls/selectlistdx";
import BoxDX from "../../../components/layout/boxdx";
import useUserService from "../../../shared/services/userservices";

interface SKUItem {
  warehouseName: string;
  warehouseAddress: string;
  skuCode: string;
  skuName: string;
  quantity: number;
}

const SKUReport = () => {
  const { t } = useTranslation();
  const { userData } = useAuthContext();
  const { setError } = useNotificationContext();
  const { getActiveUsers } = useUserService();
  const { getReportsForCustomer } = useReportService();

  const [rows, setRows] = useState<Record<string, SKUItem[]>>({});
  const [customers, setCustomers] = useState<{ text: string; value: string }[]>(
    []
  );
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columns: GridColDef[] = [
    { field: "skuCode", headerName: t("SKU Code") },
    { field: "skuName", headerName: t("Name"), flex: 1 },
    { field: "quantity", headerName: t("Quantity") },
  ];

  // load customer list if super‐user
  useEffect(() => {
    if (userData.userType === "S") {
      getActiveUsers()
        .then((res) => {
          const custs = res
            .filter((u: any) => u.userType === "C")
            .map((u: any) => ({ text: u.fullName, value: u.customerId }));
          setCustomers(custs);
        })
        .catch(setError);
    }
  }, [userData.userType]);

  // fetch report whenever customerId changes (or on mount for C‐type)
  useEffect(() => {
    const id = userData.userType === "C" ? userData.customerId : customerId;
    if (!id) return;

    setIsLoading(true);
    getReportsForCustomer(id)
      .then((apiData: SKUItem[]) => {
        // group by warehouseName exactly like old code
        const grouped = apiData.reduce<Record<string, SKUItem[]>>(
          (acc, item) => {
            (acc[item.warehouseName] = acc[item.warehouseName] || []).push(
              item
            );
            return acc;
          },
          {}
        );
        setRows(grouped);
      })
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [customerId, userData.customerId, userData.userType]);

  const handleInputChange = (e: any) => {
    setCustomerId(e.target.value);
  };

  const { toPDF, targetRef } = usePDF({
    filename: t("SKU Report"),
    page: { margin: 10, format: "a4", orientation: "portrait" },
  });

  return (
    <div ref={targetRef}>
      {(isPdfGenerating || isLoading) && (
        <Loading styles={{ height: "100%", width: "100%", left: 0 }} />
      )}
      <GridDX container columnSpacing={1} rowSpacing={2} sx={{ width: "100%" }}>
        <GridDX item xs={6} sx={{ marginBottom: 5 }}>
          <PageTitle />
        </GridDX>
        <GridDX
          item
          xs={6}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {!isPdfGenerating && (
            <ButtonDX
              disabled={Object.keys(rows).length === 0}
              onClick={() => downloadPDF({ toPDF, setIsPdfGenerating })}
            >
              {t("Download Report")}
            </ButtonDX>
          )}
        </GridDX>
        {(userData.userType === "S" || userData.userType === "T") && (
          <GridDX
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <BoxDX width={350}>
              <SelectListDX
                label={t("Select Customer")}
                name="customerId"
                items={customers}
                value={customerId}
                onChange={handleInputChange}
              />
            </BoxDX>
          </GridDX>
        )}

        {Object.values(rows).map((group) => (
          <React.Fragment key={group[0].warehouseName}>
            <GridDX item xs={3}>
              <TypographyDX fontWeight={"bold"}>{t("Warehouse")}</TypographyDX>
              <TypographyDX>{`: ${group[0].warehouseName}`}</TypographyDX>
            </GridDX>
            <GridDX item xs={9}>
              <TypographyDX fontWeight={"bold"}>{t("Address")}</TypographyDX>
              <TypographyDX>{`: ${group[0].warehouseAddress}`}</TypographyDX>
            </GridDX>
            <GridDX item xs={12}>
              <StaticDataGridDX
                rows={group}
                columns={columns}
                getRowId={(row: any) => row.skuCode}
                isLoading={isLoading}
              />
            </GridDX>
          </React.Fragment>
        ))}
      </GridDX>
    </div>
  );
};

export default SKUReport;
